import {SubAPI} from "./SubAPI";
import {sleep} from "nate-react-api-helpers";

export class SupplierCategories extends SubAPI {

    listDefaults() {
        return this.fetcher.get<SupplierCategory[]>("/api/project-supplier-category-defaults", {});
    }

    upsertDefault(input: Partial<SupplierCategory>) {
        return this.fetcher.post<number>("/api/project-supplier-category-default", input);
    }

    reSortDefault(input: { ids: number[] }) {
        return this.fetcher.post<void>("/api/project-supplier-category-default/re-sort", input);
    }

    listForProject(input: { project: number; alternative?: number; previewChangeset?: number }) {
        return this.fetcher.get<ProjectSupplierCategory[]>("/api/project-supplier-categories", input);
    }

    // runs as a background task (the handler recalcs products after a by-others
    // change); post kicks it off, then we poll for the updated category.
    async upsertForProject(input: ProjectSupplierCategory) {
        const {task} = await this.fetcher.post<{task: string}>("/api/project-supplier-category", input);

        await sleep(500);

        while (true) {
            const status = await this.fetcher.get<{done: boolean; result?: any}>("/api/util/get-background-task", {task, withResult: true});
            if (status.done) {
                if (status.result?.error) throw new Error(status.result.error);
                return status.result as ProjectSupplierCategory;
            }

            await sleep(1000);
        }
    }
}

export type SupplierCategory = {
    id: number;
    name: string;
    defaultSuppliedByUs: boolean;
    archived: boolean;
    internalLabel: SupplierCategoryInternalLabel | null;
    parent: number | null;
    parentName: string | null;
    sortOrder: number;
}

// prefixedName renders a category with its parent, e.g.
// "Door Supplier / Wood Door Supplier", using the backend-provided parentName.
export function prefixedName<T extends { name: string; parentName: string | null }>(cat: T): string {
    return cat.parentName ? `${cat.parentName} / ${cat.name}` : cat.name;
}

export type SupplierCategoryNode<T> = T & { children: SupplierCategoryNode<T>[]; depth: number };

// buildSupplierTree nests a flat list by parent, ordered by sortOrder within
// each level. Orphans (missing parent) are treated as top-level.
export function buildSupplierTree<T extends { id: number; parent: number | null; sortOrder: number }>(all: T[]): SupplierCategoryNode<T>[] {
    const byId = new Map<number, SupplierCategoryNode<T>>();
    all.forEach(c => byId.set(c.id, Object.assign({}, c, {children: [], depth: 0})));

    const roots: SupplierCategoryNode<T>[] = [];
    byId.forEach(node => {
        const parent = node.parent !== null ? byId.get(node.parent) : undefined;
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    const sortRec = (nodes: SupplierCategoryNode<T>[], depth: number) => {
        nodes.sort((a, b) => a.sortOrder - b.sortOrder);
        nodes.forEach(n => {
            n.depth = depth;
            sortRec(n.children, depth + 1);
        });
    };
    sortRec(roots, 0);

    return roots;
}

// flattenSupplierTree walks the tree depth-first so parents render immediately
// above their (indented) children while staying a single flat list.
export function flattenSupplierTree<T>(nodes: SupplierCategoryNode<T>[]): SupplierCategoryNode<T>[] {
    const out: SupplierCategoryNode<T>[] = [];
    const walk = (list: SupplierCategoryNode<T>[]) => {
        list.forEach(n => {
            out.push(n);
            walk(n.children);
        });
    };
    walk(nodes);
    return out;
}

export type SupplierCategoryInternalLabel = "door-provider" | "frame-provider" | "hardware-provider" | "standard-hardware-provider" | "aluminum-hardware-provider"
    | "wd-door-provider" | "hm-door-provider" | "al-door-provider" | "ex-door-provider"
    | "wd-frame-provider" | "hm-frame-provider" | "al-frame-provider" | "ex-frame-provider"
    | "glazing-provider";

// Aluminum and Existing door/frame suppliers are always supplied by others; their
// "By Us" override is force-unchecked and locked in the UI.
const forcedByOthersLabels: SupplierCategoryInternalLabel[] =
    ["al-door-provider", "al-frame-provider", "ex-door-provider", "ex-frame-provider"];

export function forcesByOthers(cat: { internalLabel: SupplierCategoryInternalLabel | null }): boolean {
    return !!cat.internalLabel && forcedByOthersLabels.includes(cat.internalLabel);
}

export type ProjectSupplierCategory = SupplierCategory & {
    overrideId: number | null;
    project: number;
    alternative?: number;
    byUs: boolean;
    note: string;

    backup?: ProjectSupplierCategory;
}
